import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import { Switch } from '@verlo/shared/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { Plus, X, ChevronUp, ChevronDown } from 'lucide-react';
import { chooseDefault, switchRule } from '../../lib/choicePrices';

/**
 * The item's own choices (modifiers plan, 21 Sep 2026), as the sheet draws them: a
 * choose-one group is a row of cells (a Spirit, a Heat, a size, a milk) with a price on
 * each and a default; an "any" group is tick tiles (Extras) that may count. A choose-one
 * group can offer a double — a second measure of the chosen option, priced per option.
 *
 * Shape: [{id, label, rule: 'one'|'any', double_label?, options: [{id, name, price,
 * double_price?, qty?, revenue_line?, default?}]}]. Stored prices are deltas on the item's price.
 *
 * Entered as the till sells them (Martin, 27 Oct, `lib/choicePrices`): a choose-one option is
 * typed as its own till price (`till`, Bottle £20.00), and its default is the item's price, so
 * editing either edits both. An extra is typed as what it adds (+£0.50). A double adds to the
 * chosen option, as before.
 */

const uid = () => Math.random().toString(36).slice(2, 10);
const LINES = [['', 'Same as item'], ['food', 'Food'], ['drink', 'Drink'], ['other', 'Other']];

const ModifierGroupsEditor = ({ value = [], onChange, library = [], basePrice = 0, priceText, onBasePrice }) => {
  const groups = value;
  const set = (i, patch) => onChange(groups.map((g, j) => (j === i ? { ...g, ...patch } : g)));
  const setOpt = (i, k, patch) => set(i, { options: groups[i].options.map((o, m) => (m === k ? { ...o, ...patch } : o)) });
  const addGroup = (rule) => onChange([...groups, { id: `g-${uid()}`, label: rule === 'one' ? 'Choice' : 'Extras', rule, options: [] }]);
  const addOpt = (i, fromLib) => {
    const one = groups[i].rule === 'one';
    const first = one && groups[i].options.length === 0;
    set(i, { options: [...groups[i].options, {
      id: `o-${uid()}`, name: fromLib?.name || '', price: fromLib?.price || 0, revenue_line: fromLib?.revenue_line || '', qty: !!fromLib?.qty, default: first,
      // A new choose-one option starts at the item's price (plus the library's difference).
      ...(one ? { till: (Math.round((Number(basePrice || 0) + Number(first ? 0 : fromLib?.price || 0)) * 100) / 100).toFixed(2) } : {}),
    }] });
  };
  const replace = (i, group) => onChange(groups.map((g, j) => (j === i ? group : g)));
  const makeDefault = (i, k) => {
    const { group, base } = chooseDefault(groups[i], k, basePrice);
    replace(i, group);
    onBasePrice?.(base.toFixed(2), { follow: false });
  };
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= groups.length) return; const next = [...groups]; [next[i], next[j]] = [next[j], next[i]]; onChange(next); };
  const offersDouble = (g) => g.options.some((o) => o.double_price != null && o.double_price !== '');

  return (
    <div className="space-y-3" data-testid="modifier-groups">
      {groups.length === 0 && <p className="text-[13px] text-ink-faint">No choices yet. A choose-one group is a row of cells on the till (Spirit, Heat); extras are tick tiles.</p>}
      {groups.map((g, i) => (
        <div key={g.id} className="rounded-lg border border-hairline bg-surface-2 p-3" data-testid={`modifier-group-${i}`}>
          <div className="flex flex-wrap items-center gap-2">
            <Input value={g.label} onChange={(e) => set(i, { label: e.target.value })} className="h-9 w-40 bg-white font-semibold" placeholder="Group name" data-testid={`modifier-group-${i}-label`} />
            <Segmented value={g.rule} onChange={(v) => replace(i, switchRule(g, v, basePrice))} options={[{ value: 'one', label: 'Choose one' }, { value: 'any', label: 'Any' }]} />
            <div className="ml-auto flex items-center gap-1">
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => move(i, -1)} aria-label="Move up"><ChevronUp size={14} /></Button>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => move(i, 1)} aria-label="Move down"><ChevronDown size={14} /></Button>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-ink-faint hover:text-signal-stop" onClick={() => onChange(groups.filter((_, j) => j !== i))} aria-label="Remove group"><X size={14} /></Button>
            </div>
          </div>

          <div className="mt-2 grid gap-1.5">
            {g.options.length > 0 && (
              <div className={`grid gap-2 px-1 text-[11px] font-bold uppercase tracking-[0.05em] text-ink-faint ${g.rule === 'one' ? 'grid-cols-[1fr_88px_96px_128px_60px_28px]' : 'grid-cols-[1fr_88px_128px_60px_28px]'}`}>
                <span>Option</span><span>{g.rule === 'one' ? 'Till price' : 'Adds'}</span>{g.rule === 'one' && <span>Double adds</span>}<span>Line</span><span>{g.rule === 'one' ? 'Default' : 'Counts'}</span><span />
              </div>
            )}
            {g.rule === 'one' && g.options.length > 0 && (
              <p className="px-1 text-[12px] text-ink-faint" data-testid={`modifier-group-${i}-price-hint`}>Each price is what the till charges for that option. The default&apos;s is the item&apos;s price.</p>
            )}
            {g.options.map((o, k) => (
              <div key={o.id} className={`grid items-center gap-2 ${g.rule === 'one' ? 'grid-cols-[1fr_88px_96px_128px_60px_28px]' : 'grid-cols-[1fr_88px_128px_60px_28px]'}`} data-testid={`modifier-group-${i}-option-${k}`}>
                <Input value={o.name} onChange={(e) => setOpt(i, k, { name: e.target.value })} className="h-9 bg-white" placeholder="Name" autoComplete="off" />
                {g.rule === 'one' ? (
                  // Every option shows what the till charges for it. Typing the default's also
                  // sets the item's price above, which is the rule; it used to show the item's
                  // price instead of its own, which hid a default that carries its own. Both
                  // go in one change, so the options that follow the price still move.
                  <Input type="number" step="0.01" inputMode="decimal" value={o.till ?? ''}
                    onChange={(e) => (o.default && onBasePrice
                      ? onBasePrice(e.target.value, { till: [i, k] })
                      : setOpt(i, k, { till: e.target.value }))}
                    className="h-9 bg-white font-mono tabular-nums" placeholder="0.00" aria-label={`${o.name || 'Option'} till price`}
                    title={o.default ? "The default's price is the item's price" : undefined} data-testid={`modifier-group-${i}-option-${k}-price`} />
                ) : (
                  <Input type="number" step="0.01" inputMode="decimal" value={o.price ?? 0} onChange={(e) => setOpt(i, k, { price: e.target.value })}
                    className="h-9 bg-white font-mono tabular-nums" placeholder="0.00" aria-label={`${o.name || 'Option'} adds`}
                    title={`Adds £${Number(o.price || 0).toFixed(2)} to whatever's chosen`} data-testid={`modifier-group-${i}-option-${k}-price`} />
                )}
                {g.rule === 'one' && (
                  <Input type="number" step="0.01" inputMode="decimal" value={o.double_price ?? ''} onChange={(e) => setOpt(i, k, { double_price: e.target.value === '' ? null : e.target.value })} className="h-9 bg-white font-mono tabular-nums" placeholder="—" title="Price of a double of this option, on top" />
                )}
                <Select value={o.revenue_line || 'inherit'} onValueChange={(v) => setOpt(i, k, { revenue_line: v === 'inherit' ? '' : v })}>
                  <SelectTrigger className="h-9 bg-white text-[12.5px]"><SelectValue /></SelectTrigger>
                  <SelectContent>{LINES.map(([v, l]) => <SelectItem key={v || 'inherit'} value={v || 'inherit'}>{l}</SelectItem>)}</SelectContent>
                </Select>
                {g.rule === 'one'
                  // A choose-one group always has a default: switching one on moves it there.
                  ? <Switch checked={!!o.default} onCheckedChange={(v) => { if (v) makeDefault(i, k); }} aria-label="Default" />
                  : <Switch checked={!!o.qty} onCheckedChange={(v) => setOpt(i, k, { qty: v })} aria-label="Counts" />}
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-ink-faint hover:text-signal-stop" onClick={() => set(i, { options: g.options.filter((_, m) => m !== k) })} aria-label="Remove option"><X size={14} /></Button>
              </div>
            ))}
            {/* An option's name is plain text (Oct 2026): it had the whole library as browser
                suggestions, so a new size on Lager opened like a dropdown. The library is
                reached on purpose, from the picker beside Add option. */}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={() => addOpt(i)} data-testid={`modifier-group-${i}-add`}><Plus size={14} className="mr-1" />Add option</Button>
              {library.length > 0 && (
                <Select value="" onValueChange={(id) => addOpt(i, library.find((m) => m.id === id))}>
                  <SelectTrigger className="h-9 w-52 bg-white text-[12.5px]"><SelectValue placeholder="…or from the library" /></SelectTrigger>
                  <SelectContent>{library.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}{m.price ? ` (${m.price > 0 ? '+' : '−'}£${Math.abs(m.price).toFixed(2)})` : ''}</SelectItem>)}</SelectContent>
                </Select>
              )}
              {g.rule === 'one' && (
                <label className="flex items-center gap-2 text-[12.5px] text-ink-soft" title="Print the default on the ticket even when nobody changed it: a cooking temperature the kitchen must never guess.">
                  <Switch checked={!!g.print_default} onCheckedChange={(v) => set(i, { print_default: v })} aria-label="Print the default" />
                  Print the default
                </label>
              )}
              {g.rule === 'one' && offersDouble(g) && (
                <div className="ml-auto flex items-center gap-2 text-[12.5px] text-ink-soft">
                  Double tile says
                  <Input value={g.double_label || ''} onChange={(e) => set(i, { double_label: e.target.value })} className="h-8 w-40 bg-white" placeholder="e.g. Double measure" />
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={() => addGroup('one')} data-testid="modifier-groups-add-one"><Plus size={14} className="mr-1" />Choose-one group</Button>
        <Button variant="outline" size="sm" onClick={() => addGroup('any')} data-testid="modifier-groups-add-any"><Plus size={14} className="mr-1" />Extras group</Button>
      </div>
    </div>
  );
};

/** Numbers as numbers, blanks gone — what the API stores. */
export const normaliseGroups = (groups) => (groups || []).filter((g) => g.label?.trim()).map((g) => ({
  id: g.id, label: g.label.trim(), rule: g.rule === 'any' ? 'any' : 'one',
  ...(g.double_label ? { double_label: g.double_label } : {}),
  ...(g.print_default ? { print_default: true } : {}),
  options: (g.options || []).filter((o) => o.name?.trim()).map((o) => ({
    id: o.id, name: o.name.trim(), price: parseFloat(o.price) || 0,
    ...(o.double_price != null && o.double_price !== '' ? { double_price: parseFloat(o.double_price) || 0 } : {}),
    ...(o.qty ? { qty: true } : {}),
    ...(o.revenue_line && o.revenue_line !== 'inherit' ? { revenue_line: o.revenue_line } : {}),
    ...(o.default ? { default: true } : {}),
  })),
}));

export default ModifierGroupsEditor;
